// "Monthly Mortgage Summary": pick a loan officer and a month, read exactly
// what the email will say, then send it from your own Outlook.
//
// Reversibility-first (CLAUDE.md): the one thing here that cannot be undone is
// the email, so the send button is disabled until the preview on screen matches
// the current choices, and it names the recipient. The email body is rebuilt
// on the server at send time; nothing the browser shows is trusted as content.

import { useEffect, useState } from "react";

import { ApiError, apiV11 } from "../api";
import type { MortgageMonthlySummaryRec, MortgageSummaryAdvisor, MortgageSummaryEmailRec } from "../api";
import { DEFAULT_SUMMARY_NOTE } from "@shared/comp/mortgage-monthly-summary-email";
import { Button } from "../ui";
import { Modal } from "./Modal";

type Step =
  | { kind: "pick" }
  | { kind: "loading" }
  | { kind: "preview"; key: string; summary: MortgageMonthlySummaryRec; email: MortgageSummaryEmailRec }
  | { kind: "sending"; summary: MortgageMonthlySummaryRec; email: MortgageSummaryEmailRec }
  | { kind: "sent"; to: string }
  | { kind: "error"; message: string };

const SEND_ERRORS: Record<string, string> = {
  not_connected: "Your Microsoft 365 mailbox isn't connected. Reconnect it under Preferences, Email, then try again.",
  rate_limit_exceeded: "You've hit the send-mail limit for this hour. Try again later.",
};

function messageFrom(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code && SEND_ERRORS[err.code]) return SEND_ERRORS[err.code]!;
    const data = err.data as { message?: unknown } | null;
    if (data && typeof data.message === "string") return data.message;
    return err.message;
  }
  return err instanceof Error ? err.message : "Something went wrong. Nothing was sent.";
}

function previousMonth(today: Date): string {
  const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function MortgageSummaryModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [advisors, setAdvisors] = useState<MortgageSummaryAdvisor[]>([]);
  const [advisorId, setAdvisorId] = useState("");
  const [period, setPeriod] = useState(() => previousMonth(new Date()));
  const [note, setNote] = useState(DEFAULT_SUMMARY_NOTE);
  const [step, setStep] = useState<Step>({ kind: "pick" });

  useEffect(() => {
    if (!open) return;
    setStep({ kind: "pick" });
    setNote(DEFAULT_SUMMARY_NOTE);
    apiV11
      .listMortgageSummaryAdvisors()
      .then((res) => {
        setAdvisors(res.advisors);
        setAdvisorId((current) => current || res.advisors[0]?.id || "");
      })
      .catch((err) => setStep({ kind: "error", message: messageFrom(err) }));
  }, [open]);

  const key = `${advisorId}|${period}|${note}`;
  const advisor = advisors.find((a) => a.id === advisorId) ?? null;
  const first = advisor?.name.trim().split(/\s+/)[0] ?? "";
  const busy = step.kind === "loading" || step.kind === "sending";
  const previewIsCurrent = step.kind === "preview" && step.key === key;

  async function loadPreview() {
    if (!advisorId) return;
    setStep({ kind: "loading" });
    try {
      const res = await apiV11.previewMortgageSummary({ advisor_user_id: advisorId, period, note });
      setStep({ kind: "preview", key, summary: res.summary, email: res.email });
    } catch (err) {
      setStep({ kind: "error", message: messageFrom(err) });
    }
  }

  async function send() {
    if (step.kind !== "preview" || !previewIsCurrent) return;
    setStep({ kind: "sending", summary: step.summary, email: step.email });
    try {
      const res = await apiV11.sendMortgageSummary({ advisor_user_id: advisorId, period, note });
      setStep({ kind: "sent", to: res.to });
    } catch (err) {
      setStep({ kind: "error", message: messageFrom(err) });
    }
  }

  const shown = step.kind === "preview" || step.kind === "sending" ? step : null;
  const fieldStyle = { minHeight: 44, padding: "0 10px", fontSize: 16, maxWidth: "100%" } as const;

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Monthly Mortgage Summary"
      data-testid="mortgage-summary-modal"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            Close
          </Button>
          {step.kind !== "sent" && (
            <Button variant="ghost" type="button" onClick={() => void loadPreview()} disabled={busy || !advisorId} data-testid="mortgage-summary-preview">
              {previewIsCurrent ? "Refresh preview" : "Preview"}
            </Button>
          )}
          {step.kind !== "sent" && (
            <Button
              variant="primary"
              type="button"
              onClick={() => void send()}
              disabled={!previewIsCurrent || busy}
              data-testid="mortgage-summary-send"
            >
              {step.kind === "sending" ? "Sending…" : `Send summary to ${first || "advisor"}`}
            </Button>
          )}
        </>
      }
    >
      <div className="stack" style={{ gap: 12 }}>
        <p className="muted" style={{ margin: 0 }}>
          Emails a loan officer their Mortgage month from your own Outlook: what closed and was commissioned, what
          closed with no commission worksheet yet, and what is still open. Preview changes nothing. The send is the
          one step you cannot take back.
        </p>

        <label htmlFor="mortgage-summary-advisor" style={{ fontWeight: 600, fontSize: 13 }}>Loan officer</label>
        <select id="mortgage-summary-advisor" value={advisorId} onChange={(e) => setAdvisorId(e.target.value)} disabled={busy} style={fieldStyle}>
          {advisors.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>

        <label htmlFor="mortgage-summary-month" style={{ fontWeight: 600, fontSize: 13 }}>Month</label>
        <input id="mortgage-summary-month" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} disabled={busy} style={{ ...fieldStyle, maxWidth: 260 }} />

        <label htmlFor="mortgage-summary-note" style={{ fontWeight: 600, fontSize: 13 }}>Note in the email</label>
        <textarea id="mortgage-summary-note" value={note} onChange={(e) => setNote(e.target.value)} disabled={busy} rows={2} maxLength={1000} style={{ padding: 10, fontSize: 16, width: "100%", boxSizing: "border-box" }} />

        {shown && (
          <div className="stack" style={{ gap: 8 }} data-testid="mortgage-summary-preview-body">
            {!previewIsCurrent && step.kind === "preview" && (
              <div style={{ color: "var(--danger)", fontSize: 13 }}>
                You changed something after this preview. Refresh the preview before sending.
              </div>
            )}
            <div style={{ fontSize: 13 }}>
              <strong>{shown.summary.commissioned.total}</strong> closed and commissioned ·{" "}
              <strong>{shown.summary.awaiting_worksheet.total}</strong> closed with no worksheet ·{" "}
              <strong>{shown.summary.still_open.total}</strong> still open
            </div>
            <div style={{ fontSize: 13 }}>
              <strong>To:</strong> {shown.email.to} · <strong>Subject:</strong> {shown.email.subject}
            </div>
            <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 13, margin: 0, padding: 12, border: "1px solid var(--mid-gray)", borderRadius: 6, background: "var(--bg-1)", maxHeight: 280, overflow: "auto" }}>
              {shown.email.text}
            </pre>
          </div>
        )}

        {step.kind === "loading" && <div>Building the summary…</div>}
        {step.kind === "sent" && (
          <div data-testid="mortgage-summary-sent">
            <strong>Sent</strong> to {step.to}. A copy is in your Sent Items.
          </div>
        )}
        {step.kind === "error" && (
          <div role="alert" style={{ color: "var(--danger)" }}>{step.message}</div>
        )}
      </div>
    </Modal>
  );
}
